/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import PropTypes from 'prop-types';
import { MsB2b } from '@api/ms-b2b';

const limit = 10;

const propTypes = {
  label: PropTypes.string,
  editable: PropTypes.bool,
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  idSales: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  isWarehouse: PropTypes.bool,
  useCallbackOptions: PropTypes.array,
};

const defaultProps = {
  label: '',
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  customFilter: {},
  value: '',
  useCallbackOptions: [],
};

const WarehouseSearch = (props) => {
  const {
    label = '',
    editable = true,
    isRequired = false,
    multiple = false,
    hasError = false,
    customFilter = {},
    value = '',
    idSales = {},
    isWarehouse = false,
    useCallbackOptions = [],
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getWarehouse = useApi(MsOrder.getWarehouse);
  const getWarehouseFilterSales = useApi(MsB2b.getSalesTeamDetail);

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading({ ...loading, loadMore: true });
    } else {
      setLoading({ ...loading, initial: true });
    }
    try {
      let filter = {
        is_active: true,
        id_sales: idSales?.id,
        is_warehouse: isWarehouse,
      };
      const SalesId = idSales?.id;

      if (customFilter && Object.keys(customFilter).length) {
        filter = {
          ...filter,
          ...customFilter,
        };
      }
      if (val) {
        filter.name = { $regex: val, $options: 'i' };
      }

      if (!val && filter.name) {
        delete filter.name;
      }

      if (filter.is_warehouse) {
        const response = await getWarehouse.request({
          params: {
            filter: filter,
            skip: newSkip,
            limit: limit,
          },
        });
        if (response.status == 200 && response?.data?.data?.length) {
          const result = response.data.data.map((item) => {
            if (item._id) {
              item.id = item._id;
            }

            return item;
          });
          if (newSkip) {
            setOptions(options.concat(result));
          } else {
            setOptions(result);
          }
        } else {
          if (!newSkip) {
            setOptions([]);
          }
        }

        if (newSkip) {
          setLoading({ ...loading, loadMore: false });
        } else {
          setLoading({ ...loading, initial: false });
        }
      } else if (filter.id_sales !== '' && filter.id_sales !== undefined) {
        const response = await getWarehouseFilterSales.request(SalesId);
        if (response.status == 200 && response?.data?.data) {
          const defaultWarehouse = response.data.data.warehouse;
          const arrDefault = [defaultWarehouse];

          const result = (response.data.data.optional_warehouse || []).map(
            (item) => {
              if (item._id) {
                item.id = item._id;
              }

              return item;
            },
          );

          const JoinArrayWarehouse = arrDefault
            .concat(result)
            .filter((el) =>
              val ? el.name.toLowerCase().includes(val.toLowerCase()) : true,
            );
          if (newSkip) {
            setOptions(options.concat(JoinArrayWarehouse));
          } else {
            setOptions(JoinArrayWarehouse);
          }
        } else {
          if (!newSkip) {
            setOptions([]);
          }
        }

        if (newSkip) {
          setLoading({ ...loading, loadMore: false });
        } else {
          setLoading({ ...loading, initial: false });
        }
      } else {
        if (newSkip) {
          setLoading({ ...loading, loadMore: false });
        } else {
          setLoading({ ...loading, initial: false });
        }
      }
    } catch (error) {
      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    }
  };

  return (
    <SelectBox
      label={label}
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : 'Select Warehouse'
      }
      trackBy="id"
      labelKey="name"
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={false} // disable for now
      loadMoreEmpty={skip >= options.length}
      useCallbackOptions={useCallbackOptions}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val) => {
        props.onChange(val);
      }}
      onReady={() => {
        setSkip(0);
        onSearch();
      }}
    />
  );
};

WarehouseSearch.propTypes = propTypes;
WarehouseSearch.defaultProps = defaultProps;

export default WarehouseSearch;
